import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Suspense} from 'react'
import {ClientOnly} from '../../ClientOnly'
import styles from '../Assignees.module.css'
import {AuthorActions} from './AuthorActions'
import {ReviewerActions} from './ReviewerActions'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {pullRequestsLayoutRoute} from '../../../routes/layout-route'
import type {ReviewRequestsData} from '../../../page-data/payloads/review-requests'
import {getCurrentUserLogin, isLoggedIn} from '@github-ui/client-env'
import {isPRAuthor, isPRReviewer} from './action-item-helpers'
import {MergeMethodContextProvider} from '@github-ui/mergebox'
import {
  useMergeStatusButtonData,
  type ValidatedMergeStatusButtonData,
} from '../../../hooks/use-merge-status-button-data'
import {useReviewers} from '../data/use-reviewers'
import {ActionItem} from './ActionItem'
import {ActionItemsLoading} from './ActionItemsLoading'

export function ActionItemsSection() {
  return (
    <ErrorBoundary
      boundaryName="ActionItemsSection"
      fallback={<span className={styles.emptyState}>Unable to load action items</span>}
    >
      <Suspense fallback={<ActionItemsLoading />}>
        <ClientOnly serverFallback={<ActionItemsLoading />}>
          <ActionItemsContent />
        </ClientOnly>
      </Suspense>
    </ErrorBoundary>
  )
}

function ActionItemsContent() {
  const {data: reviewers} = useReviewers()
  const {data: layoutData} = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')

  const {pullRequest, mergeStatusButtonData} = layoutData
  const viewerLogin = getCurrentUserLogin() ?? ''
  const authorLogin = pullRequest.author.login

  const validateMergeStatusButtonData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)

  return (
    <ActionItemsComponent
      reviewers={reviewers}
      viewerLogin={viewerLogin}
      authorLogin={authorLogin}
      mergeStatusButtonData={validateMergeStatusButtonData}
    />
  )
}

export type ActionItemsComponentProps = {
  reviewers: ReviewRequestsData
  viewerLogin: string
  authorLogin: string
  mergeStatusButtonData: ValidatedMergeStatusButtonData | null
}

export function ActionItemsComponent({
  reviewers,
  viewerLogin,
  authorLogin,
  mergeStatusButtonData,
}: ActionItemsComponentProps) {
  const loggedIn = isLoggedIn()
  const isAuthor = isPRAuthor(viewerLogin, authorLogin)
  const isReviewer = isPRReviewer(viewerLogin, reviewers)

  if (!loggedIn) {
    return <ActionItem title="Only signed-in users can review this PR" action={{linkTo: '/login', label: 'Sign in'}} />
  }

  return (
    <div>
      {isAuthor && mergeStatusButtonData && (
        // TODO - there is an edge case where there is no validated merge status data due to the opt out flag
        <MergeMethodContextProvider defaultMergeMethod={mergeStatusButtonData.defaultMergeMethod}>
          <AuthorActions
            viewerLogin={viewerLogin}
            reviewers={reviewers}
            mergeStatusButtonData={mergeStatusButtonData}
          />
        </MergeMethodContextProvider>
      )}
      {!isAuthor && <ReviewerActions reviewers={reviewers} viewerLogin={viewerLogin} isReviewer={isReviewer} />}
    </div>
  )
}
